/* eslint-disable react-hooks/exhaustive-deps */
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import type { SelectedLineItemProduct } from '@/features/sales-draft/types/normal-line-machine-types';
import { useLocale } from '@/lib/hooks/use-locale';
import { cn } from '@/lib/utils';
import type { GetCatalogProducts } from '@/server/types/infer-trpc-types';
import { api } from '@/utils/api';
import { isEqual } from 'lodash-es';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { AddProductFromCatalog } from './add-product-from-catalog';

interface Props {
    baseUrl: string;
    lastUrl: string;
    setLastUrl: (url: string) => void;
    onAddLineItem: (product: SelectedLineItemProduct, quantity: number) => void;
    className?: string;
    height?: string;
    width?: string;
}

export const Catalog: React.FC<Props> = ({
    baseUrl,
    lastUrl,
    setLastUrl,
    height = '100%',
    width = '100%',
    className,
    onAddLineItem,
}) => {
    const { t } = useLocale();
    const { toast } = useToast();
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const [currentUrl, setCurrentUrl] = useState('');
    const [newPageCount, setNewPageCount] = useState(0);
    const getProductMutation = api.platoUser.query.catalog.getProductsFromUrl.get.useMutation();
    const [currentPrimaryProduct, setSelectedPrimaryProduct] = useState<GetCatalogProducts | null>(null);
    const [pastPrimaryProducts, setPastPrimaryProducts] = useState<Record<string, GetCatalogProducts>>({});

    useEffect(() => {
        if (currentUrl) {
            getProductMutation.mutate(
                { url: currentUrl },
                {
                    onSuccess: (data) => {
                        setSelectedPrimaryProduct(data);
                        setPastPrimaryProducts((prev) => ({
                            ...prev,
                            [currentUrl]: data,
                        }));
                    },
                    onError: (error) => {
                        setSelectedPrimaryProduct(null);
                    },
                },
            );
        }
    }, [currentUrl]);

    const onAddProduct = (quantity: number) => {
        if (!(currentPrimaryProduct && currentPrimaryProduct.mainProduct)) return;
        onAddLineItem(currentPrimaryProduct?.mainProduct, quantity);

        toast({
            icon: 'success',
            variant: 'default',
            title: t('toast__product_added_title', { ns: 'catalog' }),
            description: t('toast__product_added_body', { ns: 'catalog' }).replace(
                '{{quantity}}',
                quantity.toString(),
            ),
        });
    };

    const isLoadingMutation = getProductMutation.isLoading;

    useEffect(() => {
        // Listen for messages from the iframe
        const handleMessage = (event: MessageEvent) => {
            console.log('MESSAGE EVENT', event);
            const cleanBaseUrl = new URL(baseUrl);

            if (cleanBaseUrl && event.origin.includes(cleanBaseUrl.origin)) {
                if (event.data.type === 'PAGE_UPDATE') {
                    if (!isEqual(currentUrl, event.data.url) && !!event.data.url) {
                        setNewPageCount((prev) => prev + 1);
                        setCurrentUrl(event.data.url);
                        setLastUrl(event.data.url);
                    } else {
                        console.log('NOT SETTING');
                    }
                }
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);

    const handleBack = () => {
        const message = { type: 'NAVIGATE_BACK' };
        iframeRef.current?.contentWindow?.postMessage(message, baseUrl);
    };

    const handleForward = () => {
        const message = { type: 'NAVIGATE_FORWARD' };
        iframeRef.current?.contentWindow?.postMessage(message, baseUrl);
    };

    return (
        <div className={cn('flex h-full w-full flex-col', className)}>
            <div className="flex flex-col items-center justify-start border-b border-neutral-200 ">
                <div className="flex h-12 w-full flex-row items-center space-x-2 bg-neutral-200 pl-2">
                    <Button size="icon" variant="iframe-outline" onClick={handleBack}>
                        <ChevronLeft className="h-4 w-4 text-neutral-700" />
                    </Button>
                    <Button size="icon" variant="iframe-outline" onClick={handleForward}>
                        <ChevronRight className="h-4 w-4 text-neutral-700" />
                    </Button>
                </div>

                <AddProductFromCatalog
                    pageCount={newPageCount}
                    isLoading={isLoadingMutation}
                    product={currentPrimaryProduct?.mainProduct}
                    onAddProduct={onAddProduct}
                />
            </div>

            <iframe key={lastUrl} ref={iframeRef} src={lastUrl} width={width} height={height}></iframe>
        </div>
    );
};
